import {
  Button,
  ButtonGroup,
  MenuItem,
  Select,
  TableCell,
  TableRow,
  Tooltip,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { Applicant, Column } from './ApplicantsListTable';
import { ApplicationStatus } from '../JobCard/JobStatusCard';
import { NavLink } from 'react-router-dom';
import APICaller, { MethodType } from '../common/APICaller';
import OnboardingModal from '../Onboarding/OnboardingModal';

interface ApplicantsListTableRowProp {
  applicant: Applicant;
  columns: readonly Column[];
  jobId: string | undefined;
  onStatusUpdate: () => void;
}

const ApplicantsListTableRow: React.FC<ApplicantsListTableRowProp> = ({
  applicant,
  columns,
  jobId,
  onStatusUpdate,
}) => {
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  // Use applicant's status directly for display, local state only for dropdown
  const currentStatus = applicant.status;
  const getButtonTextandColor = (status: number) => {
    switch (status) {
      case ApplicationStatus.PENDING_REVIEW:
        return {
          text: 'Pending Review',
          color: 'var(--status-under-review)',
        };
      case ApplicationStatus.PENDING_DECISION:
        return {
          text: 'Pending Decision',
          color: 'var(--status-review-completed)',
        };
      case ApplicationStatus.SHORTLISTED:
        return {
          text: 'Shortlisted',
          color: 'var(--status-shortlisted)',
        };
      case ApplicationStatus.ACCEPTED:
        return {
          text: 'Accepted',
          color: 'var(--ttg-green)',
        };
      case ApplicationStatus.REJECTED:
        return {
          text: 'Rejected',
          color: 'var(--status-not-selected)',
        };
      case ApplicationStatus.INCOMPLETE:
        return {
          text: 'Not Submitted',
          color: 'var(--ttg-black)',
        };
      default:
        return {
          text: 'N/A',
          color: 'var(--ttg-black)',
        };
    }
  };
  // const button = getButtonTextandColor(applicant.status);
  const button = useMemo(() => {
    return getButtonTextandColor(currentStatus);
  }, [currentStatus]);

  const StatusDropdown = useMemo(() => {
    const disableDropdown =
      currentStatus === ApplicationStatus.PENDING_REVIEW ||
      currentStatus === ApplicationStatus.INCOMPLETE;
    return (
      <Tooltip
        title={
          disableDropdown
            ? 'Finish grading to enable manual status change feature'
            : ''
        }
      >
        <Select
          sx={{ minWidth: '80px' }}
          disabled={disableDropdown}
          value={currentStatus}
          onChange={e => {
            // console.log(e.target.value);
            const newStatus: number = Number(e.target.value);
            APICaller(
              `/metrics/jobs/${jobId}/applicants/${applicant.id}/status/update`,
              MethodType.PUT,
              {
                status: newStatus,
              },
            ).then(res => {
              if (res.errorMessage) {
                alert(res.errorMessage);
              } else {
                // Status updated successfully - refresh the applicants list
                onStatusUpdate();
              }
            });
          }}
        >
          <MenuItem value={ApplicationStatus.SHORTLISTED}>Shortlisted</MenuItem>
          <MenuItem value={ApplicationStatus.REJECTED}>Not Selected</MenuItem>
          {/* <MenuItem value={ApplicationStatus.PENDING_REVIEW}>
              Pending Review
            </MenuItem> */}
          <MenuItem value={ApplicationStatus.PENDING_DECISION}>
            Pending Decision
          </MenuItem>
          <MenuItem value={ApplicationStatus.ACCEPTED}>Accepted</MenuItem>
          {/* <MenuItem value={ApplicationStatus.INCOMPLETE}>
              Not Submitted
            </MenuItem> */}
        </Select>
      </Tooltip>
    );
  }, [currentStatus, jobId, applicant.id, onStatusUpdate]);
  return (
    <>
      <TableRow hover role="checkbox" tabIndex={-1}>
      <TableCell key="name" align={columns[0].align}>
        {applicant.firstName} {applicant.lastName}
      </TableCell>
      <TableCell key="score" align={columns[1].align}>
        {applicant.totalGrade !== null ? applicant.totalGrade : 'N/A'}
      </TableCell>
      <TableCell key="status" align={columns[2].align}>
        <Button
          variant="contained"
          sx={{
            backgroundColor: button.color,
            '&:hover': {
              backgroundColor: button.color,
            },
            padding: '6px 12px',
            cursor: 'pointer',
            width: '150px',
            fontSize: '0.8rem',
          }}
        >
          {button.text}
        </Button>
      </TableCell>
      <TableCell key="interviews" align="center">
        <NavLink to={`/manage-jobs/applicants/${jobId}/interviews/${applicant.id}`}>
          <Button variant="outlined"
            sx={{
              minWidth: 90,
              whiteSpace: 'nowrap',
            }}> Interviews
          </Button>
        </NavLink>
      </TableCell>

      {/* Onboarding column — only active for Accepted applicants */}
      <TableCell key="onboarding" align="center">
        {currentStatus === ApplicationStatus.ACCEPTED ? (
          <Button
            variant="contained"
            size="small"
            onClick={() => setOnboardingOpen(true)}
            sx={{
              backgroundColor: '#2e7d32',
              color: 'white',
              textTransform: 'none',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              '&:hover': { backgroundColor: '#1b5e20' },
            }}
          >
            Onboarding
          </Button>
        ) : (
          <span style={{ color: '#bbb', fontSize: '0.8rem' }}>—</span>
        )}
      </TableCell>

      <TableCell key="actions" align={columns[3].align} sx={{ whiteSpace: 'nowrap' }}>
        <ButtonGroup color="secondary" sx={{ maxHeight: '36px', flexWrap: 'nowrap' }}>
          <NavLink to={`/profile-view/${applicant.id}`} state={{ jobId: jobId }}>
            <Button>Profile</Button>
          </NavLink>
          {currentStatus !== ApplicationStatus.PENDING_REVIEW ? (
            <Tooltip title="Applicant is graded. Click to view the results.">
              <NavLink
                to={`/grade-assessment-list/job/${jobId}/applicant/${applicant.id}?view=true`}
              >
                {/* <Button>Grade</Button> */}
                <Button
                  sx={{
                    whiteSpace: 'nowrap',
                    backgroundColor: 'white',
                    color: 'var(--ttg-green)',
                    borderColor: 'var(--ttg-green)',
                    '&:hover': {
                      backgroundColor: '#f0f8f0', // Light green background on hover
                      borderColor: 'var(--ttg-green)',
                      color: 'var(--ttg-green)',
                    },
                  }}
                >
                  View Results
                </Button>
              </NavLink>
              {/* <span>
                <Button disabled>Grade</Button>
              </span> */}
            </Tooltip>
          ) : (
            <NavLink
              to={`/grade-assessment-list/job/${jobId}/applicant/${applicant.id}`}
            >
              <Button>Grade</Button>
            </NavLink>
          )}
          {StatusDropdown}
        </ButtonGroup>
      </TableCell>
    </TableRow>

    {/* Onboarding modal */}
    <OnboardingModal
      open={onboardingOpen}
      onClose={() => setOnboardingOpen(false)}
      applicantId={applicant.id}
      applicantName={`${applicant.firstName} ${applicant.lastName}`}
    />
    </>
  );
};

export default ApplicantsListTableRow;
